import { fromLonLat } from 'ol/proj';

/**
 * telemetry.json (backend: src/app/services/video/flight_telemetry.py) → the drone's position, heading and camera
 * footprint at any frame of the analysed clip, and where each counted animal stood on the ground. Pure functions;
 * coordinates come out in EPSG:3857 for the map.
 *
 * telemetry.json: { fps, n_frames, time_offset_sec, height_ref, fov_deg, t: [], lon: [], lat: [], alt: [], yaw: [],
 *                   pitch: [], footprint: [[lon_tl, lat_tl, lon_tr, lat_tr, lon_br, lat_br, lon_bl, lat_bl] | null] }
 * t is the frame number in the analysed clip, like tracks.json; footprint corners are in image order (top-left first).
 */

/** Index the samples, or null when the file has no positions. */
export const indexFlight = doc => {
	const n = doc?.t?.length || 0;
	if (!n) return null;
	const t = Float64Array.from(doc.t);
	const pos = doc.t.map((_, i) => fromLonLat([doc.lon[i], doc.lat[i]]));
	const fp = (doc.footprint || []).map(c =>
		c ? [0, 2, 4, 6].map(k => fromLonLat([c[k], c[k + 1]])) : null
	);
	return {
		doc,
		t,
		pos,
		fp,
		yaw: doc.yaw || [],
		alt: doc.alt || [],
		hasFootprint: fp.some(Boolean),
		nFrames: doc.n_frames || doc.t[n - 1] + 1,
	};
};

/** The sample pair around frame f and the blend between them. */
const bracket = (flight, f) => {
	const { t } = flight;
	if (f <= t[0]) return [0, 0, 0];
	const last = t.length - 1;
	if (f >= t[last]) return [last, last, 0];
	let lo = 0;
	let hi = last;
	while (hi - lo > 1) {
		const mid = (lo + hi) >> 1;
		if (t[mid] <= f) lo = mid;
		else hi = mid;
	}
	return [lo, hi, (f - t[lo]) / (t[hi] - t[lo])];
};

const lerp = (a, b, w) => [a[0] + (b[0] - a[0]) * w, a[1] + (b[1] - a[1]) * w];

const lerpAngle = (a, b, w) => {
	const d = ((((b - a) % 360) + 540) % 360) - 180;
	return a + d * w;
};

/** Drone position, camera heading (deg, clockwise from north), height and footprint ring at frame f. */
export const flightAt = (flight, f) => {
	const [i, j, w] = bracket(flight, f);
	const a = flight.fp[i];
	const b = flight.fp[j];
	let footprint = null;
	if (a && b) footprint = a.map((p, k) => lerp(p, b[k], w));
	else footprint = a || b || null;
	return {
		position: lerp(flight.pos[i], flight.pos[j], w),
		heading:
			flight.yaw[i] != null && flight.yaw[j] != null
				? lerpAngle(flight.yaw[i], flight.yaw[j], w)
				: null,
		alt: flight.alt[i],
		footprint,
		sample: w < 0.5 ? i : j,
	};
};

/**
 * Ground point of image position (u, v) (0-1 across / down the frame) in a footprint (corners TL, TR, BR, BL): the
 * perspective mapping of the unit square onto the four corners, so a tilted camera's far rows are stretched as on
 * the ground (flat ground assumed).
 */
export const groundOf = (footprint, u, v) => {
	const [p0, p1, p2, p3] = footprint;
	// relative to the top-left corner: EPSG:3857 values are large
	const [x1, y1] = [p1[0] - p0[0], p1[1] - p0[1]];
	const [x2, y2] = [p2[0] - p0[0], p2[1] - p0[1]];
	const [x3, y3] = [p3[0] - p0[0], p3[1] - p0[1]];
	const dx3 = x2 - x1 - x3; // 0 for a parallelogram
	const dy3 = y2 - y1 - y3;
	let g = 0;
	let h = 0;
	if (Math.abs(dx3) > 1e-9 || Math.abs(dy3) > 1e-9) {
		const dx1 = x1 - x2;
		const dx2 = x3 - x2;
		const dy1 = y1 - y2;
		const dy2 = y3 - y2;
		const den = dx1 * dy2 - dx2 * dy1;
		g = (dx3 * dy2 - dx2 * dy3) / den;
		h = (dx1 * dy3 - dx3 * dy1) / den;
	}
	const w = g * u + h * v + 1;
	return [
		p0[0] + ((x1 + g * x1) * u + (x3 + h * x3) * v) / w,
		p0[1] + ((y1 + g * y1) * u + (y3 + h * y3) * v) / w,
	];
};

// EPSG:3857 metres per ground metre at map y (the Mercator stretch, 2x at 60 deg N)
const mercatorStretch = y => 1 / Math.cos(2 * Math.atan(Math.exp(y / 6378137)) - Math.PI / 2);

const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);

// a camera at least this steep sees an animal's outline as from above; a shallower one adds its side to the box height
const STEEP_PITCH = -60;

const median = values => {
	const s = [...values].sort((p, q) => p - q);
	const m = s.length >> 1;
	return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

/**
 * Where each counted animal stood: the median ground point of up to `maxPoints` of its track points. A walking
 * animal moves during its track, so this is where it was on average, not where it ended.
 * Returns [{ track (index into index.tracks), at: [x, y] EPSG:3857, frame: the frame it was counted, calf }].
 */
export const animalPositions = (index, flight, { maxPoints = 15, calfRatio = 0.75 } = {}) => {
	if (!index || !flight?.hasFootprint) return [];
	const out = [];
	index.tracks.forEach((tr, k) => {
		const n = tr.t.length;
		const step = Math.max(1, Math.floor(n / maxPoints));
		const xs = [];
		const ys = [];
		for (let j = 0; j < n; j += step) {
			const fp = flightAt(flight, tr.t[j]).footprint;
			if (!fp) continue;
			const g = groundOf(fp, tr.x[j] / index.width, tr.y[j] / index.height);
			xs.push(g[0]);
			ys.push(g[1]);
		}
		if (!xs.length) return;
		out.push({
			track: k,
			at: [median(xs), median(ys)],
			frame: tr.confirmed_at ?? tr.t0 ?? tr.t[0],
			calf: Number.isFinite(tr.rel_size) && tr.rel_size < calfRatio,
		});
	});
	return out;
};

/** The flight path up to frame f (all samples before it plus the drone's current position). */
export const pathUpTo = (flight, f) => {
	const [i, , w] = bracket(flight, f);
	const pts = flight.pos.slice(0, i + 1);
	if (w > 0) pts.push(flightAt(flight, f).position);
	return pts;
};

/**
 * Ground covered by the camera up to each sample, in m², from the footprints rasterised on a grid in ground metres
 * (not map units: a Mercator metre is 1 / cos(lat) ground metres). Returns a Float64Array, one value per sample.
 */
export const coverageSeries = (doc, maxCells = 2e6) => {
	const n = doc?.t?.length || 0;
	const out = new Float64Array(n);
	const rings = (doc?.footprint || []).map(c => c && [0, 2, 4, 6].map(k => [c[k], c[k + 1]]));
	const any = rings.find(Boolean);
	if (!any) return out;
	const lat0 = any[0][1];
	const lon0 = any[0][0];
	const kx = 111320 * Math.cos((lat0 * Math.PI) / 180);
	const ky = 110574;
	const local = rings.map(
		r => r && r.map(([lon, lat]) => [(lon - lon0) * kx, (lat - lat0) * ky])
	);
	let minX = Infinity;
	let minY = Infinity;
	let maxX = -Infinity;
	let maxY = -Infinity;
	for (const r of local)
		if (r)
			for (const [x, y] of r) {
				minX = Math.min(minX, x);
				minY = Math.min(minY, y);
				maxX = Math.max(maxX, x);
				maxY = Math.max(maxY, y);
			}
	const cell = Math.max(0.5, Math.sqrt(((maxX - minX) * (maxY - minY)) / maxCells));
	const nx = Math.ceil((maxX - minX) / cell) + 1;
	const ny = Math.ceil((maxY - minY) / cell) + 1;
	const grid = new Uint8Array(nx * ny);
	let covered = 0;
	for (let i = 0; i < n; i += 1) {
		const r = local[i];
		if (r) {
			const ys = r.map(p => p[1]);
			const r0 = Math.max(0, Math.floor((Math.min(...ys) - minY) / cell));
			const r1 = Math.min(ny - 1, Math.ceil((Math.max(...ys) - minY) / cell));
			for (let row = r0; row <= r1; row += 1) {
				const y = minY + (row + 0.5) * cell;
				const xs = [];
				for (let k = 0; k < 4; k += 1) {
					const [x1, y1] = r[k];
					const [x2, y2] = r[(k + 1) % 4];
					if (y1 <= y !== y2 <= y) xs.push(x1 + ((y - y1) / (y2 - y1)) * (x2 - x1));
				}
				if (xs.length < 2) continue;
				const c0 = Math.max(0, Math.ceil((Math.min(...xs) - minX) / cell - 0.5));
				const c1 = Math.min(nx - 1, Math.floor((Math.max(...xs) - minX) / cell - 0.5));
				for (let col = c0; col <= c1; col += 1) {
					const at = row * nx + col;
					if (!grid[at]) {
						grid[at] = 1;
						covered += 1;
					}
				}
			}
		}
		out[i] = covered * cell * cell;
	}
	return out;
};

/** Length of the flight path up to each sample, in ground metres. */
export const pathLengthSeries = doc => {
	const n = doc?.t?.length || 0;
	const out = new Float64Array(n);
	for (let i = 1; i < n; i += 1) {
		const kx = 111320 * Math.cos((doc.lat[i] * Math.PI) / 180);
		const dx = (doc.lon[i] - doc.lon[i - 1]) * kx;
		const dy = (doc.lat[i] - doc.lat[i - 1]) * 110574;
		out[i] = out[i - 1] + Math.hypot(dx, dy);
	}
	return out;
};

/** Footprints already swept by frame f, every `every`-th sample: the ground the camera has covered so far. */
export const sweptUpTo = (flight, f, every = 3) => {
	const out = [];
	for (let i = 0; i < flight.t.length && flight.t[i] <= f; i += every)
		if (flight.fp[i]) out.push(flight.fp[i]);
	return out;
};

/**
 * Body size of each track: in metres on the ground when the flight has footprints, else in pixels. The median over
 * up to `maxPoints` of its boxes. A camera looking down (pitch -60 deg or steeper) measures the box's longer side,
 * the body length; a tilted one only the side across the image, as its height also holds the animal's side.
 * Returns { unit: 'm' | 'px', across: the tilted measure was used, values: per index.tracks (NaN unknown) }.
 */
export const bodySizes = (index, flight, { maxPoints = 15 } = {}) => {
	const values = new Float64Array(index.tracks.length).fill(NaN);
	const metres = !!flight?.hasFootprint;
	const pitch = flight?.doc?.pitch || [];
	let across = false;
	index.tracks.forEach((tr, k) => {
		const n = tr.t.length;
		const step = Math.max(1, Math.floor(n / maxPoints));
		const got = [];
		for (let j = 0; j < n; j += step) {
			if (!metres) {
				got.push(Math.max(tr.w[j], tr.h[j]));
				continue;
			}
			const at = flightAt(flight, tr.t[j]);
			if (!at.footprint) continue;
			const W = index.width;
			const H = index.height;
			const [x, y, w, h] = [tr.x[j], tr.y[j], tr.w[j], tr.h[j]];
			const g = (u, v) => groundOf(at.footprint, u / W, v / H);
			const c = g(x, y);
			const k3857 = mercatorStretch(c[1]);
			const wide = dist(g(x - w / 2, y), g(x + w / 2, y)) / k3857;
			const steep = (pitch[at.sample] ?? -90) <= STEEP_PITCH;
			if (!steep) across = true;
			got.push(steep ? Math.max(wide, dist(g(x, y - h / 2), g(x, y + h / 2)) / k3857) : wide);
		}
		if (got.length) values[k] = median(got);
	});
	return { unit: metres ? 'm' : 'px', across, values };
};
